const IMPRESSION_VISIBILITY_DURATION = 1000
const IMPRESSION_VISIBILITY_RATIO = 0.5

type TrackedImpression<Impression> = {
  buildImpression: () => Impression | undefined
  element: Element
  isVisible: boolean
}

type VisibilityObserver = Pick<
  IntersectionObserver,
  'disconnect' | 'observe' | 'unobserve'
>

type CreateObserver = (
  callback: IntersectionObserverCallback,
  options: IntersectionObserverInit,
) => VisibilityObserver

export type UdpTrackingVisibilityTracker<Impression> = {
  reset: () => void
  setOnVisible: (onVisible: (impressions: Impression[]) => void) => void
  track: ({
    buildImpression,
    element,
    impressionKey,
  }: {
    buildImpression: () => Impression | undefined
    element: Element | null
    impressionKey: string
  }) => void
}

const createIntersectionObserver: CreateObserver = (callback, options) =>
  new IntersectionObserver(callback, options)

const isProductVisible = (entry: IntersectionObserverEntry): boolean =>
  entry.isIntersecting && entry.intersectionRatio >= IMPRESSION_VISIBILITY_RATIO

const createVisibilityObserver = ({
  createObserver,
  impressionKeysByElement,
  updateVisibility,
}: {
  createObserver: CreateObserver
  impressionKeysByElement: Map<Element, string>
  updateVisibility: (
    impressionKey: string,
    entry: IntersectionObserverEntry,
  ) => void
}): VisibilityObserver =>
  createObserver(
    (entries) => {
      for (const entry of entries) {
        const impressionKey = impressionKeysByElement.get(entry.target)

        if (impressionKey) {
          updateVisibility(impressionKey, entry)
        }
      }
    },
    { threshold: IMPRESSION_VISIBILITY_RATIO },
  )

export const udpTrackingVisibilityTrackerCreate = <Impression>({
  createObserver = createIntersectionObserver,
  onVisible = (): void => undefined,
}: {
  createObserver?: CreateObserver
  onVisible?: (impressions: Impression[]) => void
} = {}): UdpTrackingVisibilityTracker<Impression> => {
  const trackedImpressions = new Map<string, TrackedImpression<Impression>>()
  const impressionKeysByElement = new Map<Element, string>()
  const sentImpressionKeys = new Set<string>()
  let observer: VisibilityObserver | undefined
  let visibilityTimer: ReturnType<typeof setTimeout> | undefined
  let onVisibleCallback = onVisible

  const clearVisibilityTimer = (): void => {
    clearTimeout(visibilityTimer)
    visibilityTimer = undefined
  }

  const stopTracking = (impressionKey: string): void => {
    const tracking = trackedImpressions.get(impressionKey)

    if (!tracking) {
      return
    }

    observer?.unobserve(tracking.element)
    impressionKeysByElement.delete(tracking.element)
    trackedImpressions.delete(impressionKey)
  }

  const notifyVisibleImpressions = (): void => {
    visibilityTimer = undefined

    const impressions: Impression[] = []

    for (const [impressionKey, tracking] of trackedImpressions) {
      if (!tracking.isVisible) {
        // eslint-disable-next-line no-continue -- don't reference, refactor if touched
        continue
      }

      const impression = tracking.buildImpression()

      stopTracking(impressionKey)

      if (impression) {
        sentImpressionKeys.add(impressionKey)
        impressions.push(impression)
      }
    }

    if (impressions.length > 0) {
      onVisibleCallback(impressions)
    }
  }

  const restartVisibilityTimer = (): void => {
    clearVisibilityTimer()

    if (trackedImpressions.values().some((tracking) => tracking.isVisible)) {
      visibilityTimer = setTimeout(
        notifyVisibleImpressions,
        IMPRESSION_VISIBILITY_DURATION,
      )
    }
  }

  const updateVisibility = (
    impressionKey: string,
    entry: IntersectionObserverEntry,
  ): void => {
    const tracking = trackedImpressions.get(impressionKey)

    if (!tracking) {
      return
    }

    const isVisible = isProductVisible(entry)

    if (tracking.isVisible === isVisible) {
      return
    }

    tracking.isVisible = isVisible
    restartVisibilityTimer()
  }

  const track: UdpTrackingVisibilityTracker<Impression>['track'] = ({
    buildImpression,
    element,
    impressionKey,
  }) => {
    stopTracking(impressionKey)

    if (!element || sentImpressionKeys.has(impressionKey)) {
      return
    }

    trackedImpressions.set(impressionKey, {
      buildImpression,
      element,
      isVisible: false,
    })
    impressionKeysByElement.set(element, impressionKey)
    observer ??= createVisibilityObserver({
      createObserver,
      impressionKeysByElement,
      updateVisibility,
    })
    observer.observe(element)
  }

  const reset = (): void => {
    clearVisibilityTimer()
    observer?.disconnect()
    observer = undefined
    trackedImpressions.clear()
    impressionKeysByElement.clear()
    sentImpressionKeys.clear()
  }

  const setOnVisible = (
    nextOnVisible: (impressions: Impression[]) => void,
  ): void => {
    onVisibleCallback = nextOnVisible
  }

  return {
    reset,
    setOnVisible,
    track,
  }
}
